// Phase 7 D3 — Track C T-C4. useDevices hook.
//
// Q-11: consumes the existing Phase 5 `/api/auth/devices` shape unchanged.
// Three operations:
//   - list    GET    /api/auth/devices
//   - revoke  DELETE /api/auth/devices/:id        (idempotent 200)
//   - all     POST   /api/auth/devices/all-but-current
//
// Mutations invalidate the list query so the UI refetches on success.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  DevicesResponse,
  RevokeAllButCurrentBody,
} from '@dailydeck/shared/types';
import { apiClient } from '../app/apiClient.js';

export const DEVICES_QUERY_KEY = ['auth', 'devices'] as const;

export function useDevices(): UseQueryResult<DevicesResponse, Error> {
  return useQuery<DevicesResponse, Error>({
    queryKey: DEVICES_QUERY_KEY,
    queryFn: () => apiClient.auth.listDevices(),
  });
}

export function useRevokeDevice(): UseMutationResult<
  { ok: boolean },
  Error,
  { id: string }
> {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.auth.revokeDevice(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
    },
  });
}

export function useRevokeAllButCurrent(): UseMutationResult<
  { ok: boolean },
  Error,
  RevokeAllButCurrentBody | void
> {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, RevokeAllButCurrentBody | void>({
    mutationFn: (body) => apiClient.auth.revokeAllButCurrent(body ?? {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
    },
  });
}
